import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Eye, EyeOff, Loader2, Lock, MessageCircle, User } from 'lucide-react';

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { login, loginRequestSchema, type LoginRequest } from '@/features/auth/login.api';
import { sanitizeRedirectTarget } from '@/features/auth/redirect-safety';
import { useAuthStore } from '@/store/auth';

/**
 * `/login` — the only unauthenticated route in the console.
 *
 * Bespoke neumorphic surface: an animated soft-gradient backdrop with a raised
 * card holding the brand mark, email + password fields with zod validation,
 * and a soft-UI submit control. On success it persists the session + navigates
 * to the post-login destination (the `?redirectTo=` query if the protected
 * boundary set one, else `/`) — run through `sanitizeRedirectTarget` first
 * since `redirectTo` is attacker-controllable (see `redirect-safety.ts`).
 *
 * This is a skin-only surface: the shared Card/Button primitives are replaced
 * with inline-styled divs/buttons, but the shadcn Form/Input wrappers, the zod
 * resolver, and the login mutation are preserved verbatim so validation and
 * error handling behave exactly as before.
 *
 * Error handling follows CTO guardrail #1: NO toast, NO dialog — just an
 * inline message under the form. "Invalid email or password" is intentionally
 * vague (mirrors the Hub's opaque 401) so attackers can't enumerate emails —
 * but ONLY for a real credential rejection (400/401/422); a 5xx or network
 * error (e.g. the Hub being unreachable) says so explicitly instead of
 * misreporting itself as a bad password (T-LOGIN, bug 5 — see the login
 * mutation's `onError` below for the full mapping).
 *
 * `?reason=` carries WHY the console landed here when it wasn't a voluntary
 * sign-out — set by the axios 401 interceptor (`reason=expired`, a plain
 * session timeout / bad token) or by the realtime WS-4002 fast path
 * (`reason=elsewhere`, single-device-login kicked this session because the
 * agent signed in on another device — see `lib/realtime/useRealtime.ts`).
 * Both render a calm inline notice above the form, never a toast/dialog —
 * same guardrail as the form's own error handling.
 */
const REASON_MESSAGES: Record<string, string> = {
  expired: 'Session expired — please sign in again.',
  elsewhere: 'You were signed out because your account signed in on another device.',
};

export function LoginPage(): React.JSX.Element {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const setSession = useAuthStore((s) => s.setSession);
  const [formError, setFormError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const reason = searchParams.get('reason');
  const reasonMessage = reason ? REASON_MESSAGES[reason] : undefined;

  const form = useForm<LoginRequest>({
    resolver: zodResolver(loginRequestSchema),
    defaultValues: { email: '', password: '' },
  });

  const mutation = useMutation({
    mutationFn: login,
    onSuccess: (data) => {
      setSession({ accessToken: data.access_token, agent: data.agent });
      // Open-redirect guard: only a same-origin, app-relative path is ever
      // navigated to — see `redirect-safety.ts` for the full threat model
      // and the payloads it rejects (scheme-relative "//", "javascript:",
      // percent-encoded variants, etc.).
      const redirectTo = sanitizeRedirectTarget(searchParams.get('redirectTo'));
      navigate(redirectTo, { replace: true });
    },
    onError: (error: unknown) => {
      // Map all failure cases to an inline form-level message. Never a toast,
      // never a dialog, never a thrown stacktrace.
      //
      // T-LOGIN (01-disconnect-early-warning.md §3.6 item 7 / bug 5): during
      // an 11-day hub outage nginx returned 502 for every /api call, and this
      // branch used to map ANY axios response — 502/503 included — to
      // "Invalid email or password", so agents thought their passwords were
      // wrong. Three buckets now, verbatim from the spec:
      //   - 400/401/422 — credential rejection. The opaque wording is on
      //     purpose: 401 (bad pw, unknown email, OR a disabled agent — the
      //     Hub returns the SAME opaque 401 for all three, see
      //     `hub/app/api/auth.py::login`'s `agent.disabled` branch) and
      //     validation 400/422 all collapse to the same message so a probe
      //     can't enumerate accounts. No 403 mapping exists here because the
      //     login endpoint never returns one.
      //   - no response at all (network error, timeout, DNS failure), or any
      //     status >= 500 (hub down, nginx 502/503/504) — service-unavailable,
      //     explicitly NOT a credential error.
      //   - any other status — spelled out rather than misreported as bad
      //     credentials.
      const SERVICE_UNAVAILABLE = '服务暂时不可用，请稍后重试（不是密码错误）';
      if (axios.isAxiosError(error)) {
        const responseStatus = error.response?.status;
        if (responseStatus === undefined) {
          setFormError(SERVICE_UNAVAILABLE);
        } else if (responseStatus === 400 || responseStatus === 401 || responseStatus === 422) {
          setFormError('Invalid email or password');
        } else if (responseStatus >= 500) {
          setFormError(SERVICE_UNAVAILABLE);
        } else {
          setFormError(`登录失败（${responseStatus}），请稍后重试`);
        }
      } else {
        setFormError(SERVICE_UNAVAILABLE);
      }
    },
  });

  function onSubmit(values: LoginRequest): void {
    setFormError(null);
    mutation.mutate(values);
  }

  return (
    <div className="flex min-h-screen w-full items-center justify-center px-4 bg-[linear-gradient(120deg,#ccd8ef,#bcd9d4,#b3ddc7,#c3d2ee,#ccd8ef)] bg-[length:300%_100%] [animation:bgmove_14s_ease_infinite] dark:bg-gradient-to-br dark:from-slate-950 dark:via-slate-900 dark:to-slate-950">
      <div className="w-full max-w-sm rounded-[34px] px-11 py-12 flex flex-col gap-6 bg-[linear-gradient(150deg,#e2e8f3,#dde4f0,#dbe5e8)] shadow-[18px_18px_40px_rgba(158,170,194,0.55),-14px_-14px_34px_rgba(255,255,255,0.85),inset_1px_1px_2px_rgba(255,255,255,0.6)] dark:bg-slate-900 dark:bg-none dark:shadow-[0_12px_30px_rgba(0,0,0,0.45)]">
        {/* Brand head — raised tile + animated gradient wordmark. */}
        <div className="flex flex-col items-center gap-3.5">
          <div className="w-14 h-14 rounded-[18px] grid place-items-center neu-gradient shadow-neu-raised">
            <MessageCircle className="text-white" size={26} />
          </div>
          <h1 className="text-[32px] font-extrabold tracking-tight bg-[linear-gradient(120deg,#333b52,#2f9e77,#1f9fb0,#333b52)] bg-[length:250%_100%] [animation:bgmove_8s_ease_infinite] bg-clip-text text-transparent dark:bg-none dark:text-slate-100">
            iCGChat
          </h1>
        </div>

        {reasonMessage && (
          // Calm, non-error notice — role="status" (not "alert") because this
          // isn't a failure the user caused, just context for why they landed
          // here. Same guardrail as formError below: no toast, no dialog.
          <p role="status" className="text-sm text-center text-neu-sub">
            {reasonMessage}
          </p>
        )}

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-5" noValidate>
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-neu-sub font-extrabold text-sm pl-1.5">Email</FormLabel>
                  {/* relative wrapper holds the decorative icon; FormControl's
                      direct child stays the Input so the label's htmlFor + aria
                      wire to the real field (Radix Slot targets its 1st child). */}
                  <div className="relative">
                    <User className="text-neu-muted size-4 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <FormControl>
                      <Input
                        type="email"
                        autoComplete="username"
                        autoFocus
                        disabled={mutation.isPending}
                        className="h-12 rounded-full pl-11 pr-4 font-bold"
                        {...field}
                      />
                    </FormControl>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="text-neu-sub font-extrabold text-sm pl-1.5">
                    Password
                  </FormLabel>
                  <div className="relative">
                    <Lock className="text-neu-muted size-4 absolute left-4 top-1/2 -translate-y-1/2 pointer-events-none" />
                    <FormControl>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        autoComplete="current-password"
                        disabled={mutation.isPending}
                        className="h-12 rounded-full pl-11 pr-11 font-bold"
                        {...field}
                      />
                    </FormControl>
                    <button
                      type="button"
                      // aria-label deliberately omits the word "password" so it
                      // doesn't collide with getByLabelText(/password/i) queries
                      // that target the field (App.test.tsx).
                      aria-label="Toggle visibility"
                      onClick={() => setShowPassword((v) => !v)}
                      className="neu-pressable size-8 grid place-items-center rounded-full text-neu-muted absolute right-2 top-1/2 -translate-y-1/2"
                    >
                      {showPassword ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                    </button>
                  </div>
                  <FormMessage />
                </FormItem>
              )}
            />
            {formError && (
              // Single inline error under the password field — CTO guardrail
              // #1: no toast, no dialog. Role=alert so screen readers announce
              // it without us having to focus-trap.
              <p role="alert" className="text-sm font-medium text-destructive">
                {formError}
              </p>
            )}
            <button
              type="submit"
              disabled={mutation.isPending}
              className="w-full rounded-full py-4 text-center font-extrabold text-[16.5px] bg-transparent text-neu-sub cursor-pointer select-none shadow-[0_-6px_10px_rgba(255,255,255,0.9),0_6px_15px_rgba(128,140,166,0.45)] transition-all duration-100 [transition-timing-function:cubic-bezier(0.175,0.885,0.32,1.275)] hover:text-[#2f9e77] hover:scale-[1.04] active:scale-[0.98] active:text-[#2f9e77] active:shadow-[inset_0_-2px_5px_rgba(255,255,255,0.95),inset_0_3px_6px_rgba(90,102,130,0.28)] disabled:opacity-70 disabled:pointer-events-none inline-flex items-center justify-center gap-2 dark:text-slate-200"
            >
              {mutation.isPending && <Loader2 className="size-4 animate-spin" />}
              {mutation.isPending ? 'Signing in…' : 'Sign in'}
            </button>
          </form>
        </Form>

        {/* Decorative footer divider. */}
        <div className="flex items-center gap-3.5">
          <div className="flex-1 h-0.5 bg-[linear-gradient(90deg,transparent,rgba(138,148,171,0.45))]" />
          <span className="text-neu-muted text-[11px] font-bold">Agent Console</span>
          <div className="flex-1 h-0.5 bg-[linear-gradient(90deg,rgba(138,148,171,0.45),transparent)]" />
        </div>
      </div>
    </div>
  );
}
